/**
 * Collapsible table sections. One delegated listener per root, so sections rendered later (or replaced
 * by an AJAX re-render) need no re-binding; a whole new root does need a new instance.
 *
 * Markup: a root, a section per collapsible table, a trigger row inside it, and a button in that row
 * carrying the accessible name + `aria-expanded`. Collapsing is CSS — the state class lands on the
 * section, which decides what folds away (a table cannot animate rows open, hence no height juggling
 * like `components/website/ToggleAccordion`).
 *
 * ⚠ The legacy twin `collapseCompletedModule()` (`webroot/js/new/custom.js`, copy in
 * `components/common/Main.js`) is bound to `.collapse-table` on the document — a section must never
 * carry that class, or both fire on one click.
 *
 * @param {object} [options] Hook classes, for markup that cannot use the defaults below.
 * @param {Element|Document} [options.context] where to look for roots, default `document`. Pass the
 *     wrapper of an AJAX-rendered fragment so a second instance cannot re-bind a root the page already
 *     owns — two listeners on one root toggle twice and cancel out.
 * @param {string} [options.rootClass]      delegation root, default `js-table-accordion`
 * @param {string} [options.sectionClass]   the collapsible table, default `js-table-accordion-section`
 * @param {string} [options.rowClass]       clickable trigger row, default `js-table-accordion-row`
 * @param {string} [options.toggleClass]    button inside that row, default `js-table-accordion-toggle`
 * @param {string} [options.collapsedClass] state class on the section, default `is-collapsed`
 */
export default class TableAccordion {
    constructor(options = {}) {
        this.context = options.context || document;
        this.rootClass = options.rootClass || 'js-table-accordion';
        this.sectionClass = options.sectionClass || 'js-table-accordion-section';
        this.rowClass = options.rowClass || 'js-table-accordion-row';
        this.toggleClass = options.toggleClass || 'js-table-accordion-toggle';
        this.collapsedClass = options.collapsedClass || 'is-collapsed';

        this.roots = [];

        this.$onClick = this.onClick.bind(this);

        this.init();
    }

    init() {
        this.roots = [...this.context.querySelectorAll(`.${this.rootClass}`)];
        this.roots.forEach((root) => root.addEventListener('click', this.$onClick));
    }

    /**
     * @param {MouseEvent} event
     */
    onClick(event) {
        const row = event.target.closest(`.${this.rowClass}`);

        if (!row) {
            return;
        }

        const section = row.closest(`.${this.sectionClass}`);

        if (!section) {
            return;
        }

        this.toggle(section);
    }

    /**
     * @param {HTMLElement} section The collapsible table.
     */
    toggle(section) {
        const collapsed = section.classList.toggle(this.collapsedClass);
        const toggle = section.querySelector(`.${this.toggleClass}`);

        if (toggle) {
            toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
        }
    }

    destroy() {
        this.roots.forEach((root) => root.removeEventListener('click', this.$onClick));
        this.roots = [];
    }
}
